/**
 * Service de récupération de mot de passe v1.0
 * Architecture Frontend-First : Appels directs à l'IAM
 * 
 * @version 2.7.9
 */

import { 
  ApiError, 
  fetchWithTimeout, 
  getHeaders,
  getNativeAuthConfig, 
  isDebugMode,
} from './api';
import { nativeAuthService } from './nativeAuth';

import type {
  MobilePasswordInitResponse,
  MobilePasswordSelectMethodResponse,
  MobilePasswordResetResponse,
  MobilePasswordResendResponse,
} from '../types/mobile';

// ============================================
// Credentials (shared with nativeAuth)
// ============================================

function getIAMHeaders(): HeadersInit {
  const creds = nativeAuthService.getCredentials();
  if (!creds) {
    throw new ApiError('Credentials non chargés. Appelez loadCredentials() d\'abord.', 'auth');
  }

  return {
    ...getHeaders(undefined, false),
    'X-IAM-App-Key': creds.appKey,
  };
}

// ============================================
// Service
// ============================================

export const mobilePasswordService = {
  hasCredentials(): boolean {
    return nativeAuthService.hasCredentials();
  },

  async loadCredentials() {
    return nativeAuthService.loadCredentials();
  },

  async initRecovery(email: string): Promise<MobilePasswordInitResponse> {
    const config = getNativeAuthConfig();
    if (!config.iamApiUrl) {
      throw new ApiError('iamApiUrl non configurée', 'unknown');
    }

    if (!nativeAuthService.hasCredentials()) {
      await nativeAuthService.loadCredentials();
    }

    if (isDebugMode()) {
      const maskedEmail = email.includes('@') ? email.replace(/(^.).*(@.*$)/, '$1***$2') : '[redacted]';
      console.log('📤 [IAM] POST /sso/native/password/init', { email: maskedEmail });
    }

    const response = await fetchWithTimeout<MobilePasswordInitResponse>(
      `${config.iamApiUrl}/sso/native/password/init`,
      {
        method: 'POST',
        headers: getIAMHeaders(),
        body: JSON.stringify({ email }),
      },
      config.timeout || 30000
    );

    if (isDebugMode()) {
      console.log('✅ [IAM] Init recovery:', { 
        status: response.status, 
        hasOtp: !!response.otp_code,
      });
    }

    return response;
  },

  async selectMethod(
    processToken: string, 
    receiveChoice: 'email' | 'phone'
  ): Promise<MobilePasswordSelectMethodResponse> {
    const config = getNativeAuthConfig();
    if (!config.iamApiUrl) {
      throw new ApiError('iamApiUrl non configurée', 'unknown');
    }

    if (isDebugMode()) {
      console.log('📤 [IAM] POST /sso/native/password/select-method', { receiveChoice });
    }

    return fetchWithTimeout<MobilePasswordSelectMethodResponse>(
      `${config.iamApiUrl}/sso/native/password/select-method`,
      {
        method: 'POST',
        headers: getIAMHeaders(),
        body: JSON.stringify({ 
          process_token: processToken, 
          receive_choice: receiveChoice,
        }),
      },
      config.timeout || 30000
    );
  },

  validateOtpLocally(inputCode: string, storedCode: string): boolean {
    return inputCode === storedCode;
  },

  async reset(
    processToken: string,
    password: string
  ): Promise<MobilePasswordResetResponse> {
    const config = getNativeAuthConfig();
    if (!config.iamApiUrl) {
      throw new ApiError('iamApiUrl non configurée', 'unknown');
    }

    if (isDebugMode()) {
      console.log('📤 [IAM] POST /sso/native/password/reset');
    }

    return fetchWithTimeout<MobilePasswordResetResponse>(
      `${config.iamApiUrl}/sso/native/password/reset`,
      {
        method: 'POST',
        headers: getIAMHeaders(),
        body: JSON.stringify({
          process_token: processToken,
          password,
          password_confirmation: password,
        }),
      },
      config.timeout || 30000
    );
  },

  async resendOtp(processToken: string): Promise<MobilePasswordResendResponse> {
    const config = getNativeAuthConfig();
    if (!config.iamApiUrl) {
      throw new ApiError('iamApiUrl non configurée', 'unknown');
    }

    if (isDebugMode()) {
      console.log('📤 [IAM] POST /sso/native/password/resend');
    }

    return fetchWithTimeout<MobilePasswordResendResponse>(
      `${config.iamApiUrl}/sso/native/password/resend`,
      {
        method: 'POST',
        headers: getIAMHeaders(),
        body: JSON.stringify({ process_token: processToken }),
      },
      config.timeout || 30000
    );
  },
};

export { ApiError };
export default mobilePasswordService;
